import { Box, Button, CircularProgress, Grid, Typography } from "@mui/material"
import { Fragment, useEffect, useState } from "react"
import PermissionsTable from "./PermissionsTable"
import { useLocation, useNavigate, useParams } from "react-router"
import DiscardDialog from "@components/DiscardDialog"
import { FormBuilder } from "@components/FormBuilder"
import { FormProvider, useForm } from "react-hook-form"
import { getUserFormFields } from "./const"
import { useLazyGetUserByKeyQuery, useUpdateUserMutation } from "@store/services/usersService"
import { useGetOrganisationsQuery } from "@store/services"
import { GenericForm } from "@custom-types/form"
import { UpdateUserDto } from "@custom-types/user"
import { getUserUpdateDtoFromUserData } from "./utils"
import { useGetContractsQuery } from "@store/services/contractService"

export default function UserForm() {
  const navigate = useNavigate()
  const { id, orgId } = useParams()
  const location = useLocation()
  const [isDiscardOpen, setIsDiscardOpen] = useState(false)
  const formMethods = useForm<UpdateUserDto>({
    defaultValues: {
      key: "",
      active: false,
      first_name: "",
      last_name: "",
      organisation_key: "",
      last_login: undefined,
      last_updated: undefined,
    },
  })
  const [getUserData, { data: userData }] = useLazyGetUserByKeyQuery()
  const { data: organisationsData } = useGetOrganisationsQuery({})
  const { data: contracts } = useGetContractsQuery({})
  const [updateUser, { data: updateUserData }] = useUpdateUserMutation()
  const [userFormFields, setUserFormFields] = useState<GenericForm | null>(null)

  useEffect(() => {
    if (id) {
      getUserData(id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIX: deps after bulk migration
  }, [id])

  useEffect(() => {
    if (userData && organisationsData) {
      const foundOrg = organisationsData?.items.find((x) => x.key == userData.organisation_key)?.key

      formMethods.reset({
        ...getUserUpdateDtoFromUserData(userData),
        organisation_key: foundOrg,
      })

      setUserFormFields(
        getUserFormFields(
          organisationsData?.items.map((x) => ({
            id: x.key,
            value: x.key,
          })),
        ),
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIX: deps after bulk migration
  }, [userData, organisationsData])

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ANY :(
  const onSubmit = (data: any) => {
    if (userData?.key) {
      updateUser({ key: userData?.key, body: data })
    }
  }

  const onCancelClick = () => {
    setIsDiscardOpen(true)
  }

  useEffect(() => {
    if (updateUserData) {
      navigate(location.state?.from?.pathname ?? "/Administration/Users", {
        state: { openUsersSnackbar: true },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIX: deps after bulk migration
  }, [updateUserData])

  return (
    <Box>
      <Typography variant={"h6"} sx={{ pt: 1, pb: 1 }}>
        Edit User Form
      </Typography>
      {userFormFields ? (
        <FormProvider {...formMethods}>
          <form autoComplete={"off"} onSubmit={formMethods.handleSubmit(onSubmit)}>
            <Grid container={true} spacing={2} alignItems="center">
              {Object.keys(userFormFields).map((fieldName) => (
                <Fragment key={fieldName}>
                  <FormBuilder
                    field={fieldName}
                    // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
                    component={userFormFields[fieldName]}
                  />
                </Fragment>
              ))}
              <Grid item={true} xs={12}>
                <Box>
                  <Typography variant={"h6"}>Permissions</Typography>
                  {id && contracts ? (
                    <PermissionsTable email={id} contracts={contracts?.items ?? []} />
                  ) : null}
                </Box>
              </Grid>
              <Grid item={true} xs={12}>
                <Button variant="contained" type="submit">
                  Save
                </Button>
                <Button
                  sx={{ ml: 1 }}
                  variant="contained"
                  color={"error"}
                  type="button"
                  onClick={onCancelClick}
                >
                  Cancel
                </Button>
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      ) : (
        <Box sx={{ display: "flex", justifyContent: "center", my: 6 }}>
          <CircularProgress />
        </Box>
      )}
      <DiscardDialog
        open={isDiscardOpen}
        setOpen={setIsDiscardOpen}
        onAcceptClick={() => {
          if (orgId) {
            navigate(`/Administration/Organisations/${orgId}`)
          } else {
            navigate("/Administration/Users")
          }
        }}
      />
    </Box>
  )
}
